/**
 * Storezone — Global Progress Bar
 *
 * A thin top-of-viewport progress indicator that appears
 * whenever requests are in flight.
 *
 * Design principles:
 * - Fixed position, zero layout shift
 * - Uses --sz-primary token → adapts to all themes
 * - Three phases: filling (0→85%), completing (85→100%), hidden
 * - Subtle but immediately visible
 */

import { useEffect, useRef, useState } from 'react'
import { useProgress } from '@/hooks/useProgress'
import './GlobalProgressBar.css'

type Phase = 'idle' | 'filling' | 'completing'

export function GlobalProgressBar() {
  const { isLoading } = useProgress()
  const [phase, setPhase] = useState<Phase>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)

    if (isLoading) {
      setPhase('filling')
    } else {
      if (phase === 'filling') {
        setPhase('completing')
        timerRef.current = setTimeout(() => setPhase('idle'), 400)
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  if (phase === 'idle') return null

  return (
    <div
      className={`sz-progress-bar sz-progress-bar--${phase}`}
      role="progressbar"
      aria-label="Loading"
      aria-valuemin={0}
      aria-valuemax={100}
    />
  )
}
